import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ModalBody, ModalFooter} from 'reactstrap';
import {CheckCircle, Clock, Info} from '../../icons';
import {Spinner} from '../../Spinner';
import {HELP_SHOPIFY_INSTALL_LINK, LinkToArticle} from '../../help_center/LinkToArticle';
import {requestShopifyLink} from '../../../api/shopifyLinkRequestsApi';
import {ShopifyStoreField, useShopifyStoreInput} from './ShopifyStoreField';
import {ShopifyLinkActions} from './ShopifyLinkActions';
import {shopDomainOf} from './shopifySubdomain';
import {confirmationOf, installLinkOf} from './shopifyLinkRequestsModel';

const RAW = {interpolation: {escapeValue: false}};

/**
 * Pedir el link de instalación de una tienda de Shopify (tanda 23/10, `copy.md` §1). Con el flag de la
 * agencia, reemplaza al OAuth de la app pública en el modal «Conectar con Shopify» (Conexiones 2.1 y
 * clásico, el descubrimiento y la ficha del cliente) y en «Volver a autorizar» una tienda.
 *
 * Arriba, por qué este paso (con el artículo de ayuda); después la tienda y el botón. El pedido sale por
 * `POST shopify/custom-app/requests/` y la confirmación se muestra en el mismo modal, según `outcome`.
 * **Nunca llama a `afterSuccessfulSubmit`** del modal que lo monta: eso dispara «tienda conectada» o recarga
 * el select de tiendas, y acá todavía no se conectó nada. Tampoco promete un plazo: lo prepara fede a mano.
 *
 * - `agencyClientId`: el cliente, cuando se pide desde su ficha (desde Conexiones, `null`).
 * - `reconnectShop`: el subdominio de la tienda que se vuelve a autorizar. La tienda ya está elegida, así que
 *   no hay campo: el aviso la nombra. Sin subdominio (un dato viejo), se pide como un alta.
 * - `onClose`: cierra el modal desde «Entendido».
 * - `request` y `detect`: para los tests.
 */
export function ShopifyLinkRequest({agencyClientId = null, reconnectShop = null, onClose, request = requestShopifyLink, detect}) {
    const {t} = useTranslation();
    const field = useShopifyStoreInput(detect);
    const [sending, setSending] = useState(false);
    const [errorKey, setErrorKey] = useState(null);
    const [result, setResult] = useState(null);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    const fixedShop = reconnectShop ? shopDomainOf(reconnectShop) : null;
    const shop = fixedShop || (field.isDetecting ? null : shopDomainOf(field.subdomain));

    const submit = (event) => {
        event.preventDefault();
        if (!shop || sending) return;
        setSending(true);
        setErrorKey(null);
        request({shop, agencyClientId}).then((response) => {
            if (!mounted.current) return;
            setSending(false);
            if (!response.ok) {
                setErrorKey(response.errorKey);
                return;
            }
            setResult(response);
        });
    };

    if (result) return <Confirmation result={result} onClose={onClose}/>;

    return (
        <form onSubmit={submit} className="shl-request" data-testid="shopify-link-request" noValidate>
            <ModalBody>
                <div className="modal-section">
                    <div className="shl-notice" data-testid="shopify-link-notice">
                        <Info className="shl-notice__icon" size={18} aria-hidden="true"/>
                        <span className="shl-notice__text">
                            {fixedShop
                                ? t('shopify_link.reconnect_notice', {shop: fixedShop, ...RAW})
                                : t('shopify_link.notice')}
                            <LinkToArticle article={HELP_SHOPIFY_INSTALL_LINK} text={t('shopify_link.why')} plain/>
                        </span>
                    </div>
                    {!fixedShop && (
                        <ShopifyStoreField value={field.value} onChange={field.onChange} onBlur={field.onBlur}
                                           isDetecting={field.isDetecting} detectionError={field.detectionError}
                                           detectedSubdomain={field.detectedSubdomain}/>
                    )}
                    {errorKey && (
                        <div className="field-footnote mt-2 text-danger" role="alert" data-testid="shopify-link-error">
                            {t(errorKey)}
                        </div>
                    )}
                </div>
            </ModalBody>
            <ModalFooter className="shl-footer">
                <button type="submit" className="btn" disabled={!shop || sending} data-testid="shopify-link-submit">
                    {sending && <Spinner color="negative small"/>}
                    {t(sending ? 'shopify_link.submitting' : 'shopify_link.submit')}
                </button>
            </ModalFooter>
        </form>
    );
}

/**
 * La confirmación, en el mismo modal (`copy.md` §1). El ícono dice si el link ya está o se está preparando.
 * Al aparecer se lleva el foco a «Entendido» (QA-sl-fe-N05): el campo o el botón que lo tenía se desmonta, y el
 * foco caía en `body` con el modal abierto. El texto lo anuncia la región `status`.
 */
function Confirmation({result, onClose}) {
    const {t} = useTranslation();
    const {titleKey, lines, linkActions} = confirmationOf(result);
    const Icon = linkActions ? CheckCircle : Clock;
    const closeButton = useRef(null);
    useEffect(() => {
        if (closeButton.current) closeButton.current.focus();
    }, []);

    return (
        <>
            <ModalBody>
                <div className="modal-section">
                    <div className="shl-confirm" role="status" data-testid="shopify-link-confirmation">
                        <Icon className="shl-confirm__icon" size={28} aria-hidden="true"/>
                        <div className="shl-confirm__body">
                            {titleKey && <span className="shl-confirm__title">{t(titleKey)}</span>}
                            {lines.map((line) => (
                                <span key={line.key} className="shl-confirm__line">{t(line.key, {...line.args, ...RAW})}</span>
                            ))}
                            {linkActions && <ShopifyLinkActions link={installLinkOf(result.request)}/>}
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter className="shl-footer">
                <button type="button" className="btn" onClick={onClose} data-testid="shopify-link-close" ref={closeButton}>
                    {t('shopify_link.close')}
                </button>
            </ModalFooter>
        </>
    );
}

export default ShopifyLinkRequest;
